
import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Plus, Pencil, Trash, User, Users, ChevronDown } from 'lucide-react';
import MainLayout from '../layout/MainLayout';

const DEPARTMENTS = [
  'All',
  'HR',
  'Tech',
  'Finance',
  'Sales',
  'Marketing',
  'Support',
];

const departmentColors = {
  HR: 'bg-pink-100 text-pink-800',
  Tech: 'bg-blue-100 text-blue-800',
  Finance: 'bg-emerald-100 text-emerald-800',
  Sales: 'bg-amber-100 text-amber-800',
  Marketing: 'bg-purple-100 text-purple-800',
  Support: 'bg-gray-100 text-gray-800',
};

// Avatar with fallback initials and gradient
function Avatar({ src, name, size = 'md' }) {
  const initials = name
    ? name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase()
    : 'EM';
  const sizeClass = size === 'md' ? 'h-12 w-12' : 'h-8 w-8';
  return src ? (
    <img
      src={src}
      alt={name}
      className={`rounded-full object-cover ${sizeClass} border-2 border-white shadow-inner`}
    />
  ) : (
    <div
      className={`rounded-full ${sizeClass} flex items-center justify-center font-bold text-white bg-gradient-to-br from-indigo-400 to-purple-500 shadow-inner`}
    >
      {initials}
    </div>
  );
}

// IconButton with tooltip
function IconButton({ icon: Icon, tooltip, color, onClick }) {
  const colorClass =
    color === 'blue'
      ? 'text-blue-500 hover:text-blue-700'
      : color === 'red'
      ? 'text-red-500 hover:text-red-700'
      : 'text-gray-400 hover:text-gray-600';
  return (
    <button
      type="button"
      className={`inline-flex items-center p-2 rounded-full transition-colors duration-200 ${colorClass}`}
      title={tooltip}
      onClick={onClick}
    >
      <Icon className="h-5 w-5" />
    </button>
  );
}

// Demo data (replace with API)
const DEMO_EMPLOYEES = [
  {
    id: 1,
    name: 'Hashib Raja',
    email: 'hashib@extensioncrm.com',
    department: 'HR',
    role: 'HR Manager',
    joiningDate: '2022-01-15',
    profilePic: '',
  },
  {
    id: 2,
    name: 'Vishal Kumar',
    email: 'vishal@extensioncrm.com',
    department: 'Tech',
    role: 'Frontend Developer',
    joiningDate: '2021-09-10',
    profilePic: '',
  },
  {
    id: 3,
    name: 'Amit Kumar',
    email: 'amit@extensioncrm.com',
    department: 'Finance',
    role: 'Accountant',
    joiningDate: '2023-03-01',
    profilePic: '',
  },
  {
    id: 4,
    name: 'Bhawna Singh',
    email: 'bhawna@extensioncrm.com',
    department: 'Sales',
    role: 'Sales Executive',
    joiningDate: '2020-07-22',
    profilePic: '',
  },
];

export default function Employees() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [department, setDepartment] = useState('All');
  const [employees, setEmployees] = useState(DEMO_EMPLOYEES);

  // Filtered employees
  const filtered = useMemo(() => {
    return employees.filter((emp) => {
      const matchesSearch =
        emp.name.toLowerCase().includes(search.toLowerCase()) ||
        emp.email.toLowerCase().includes(search.toLowerCase()) ||
        emp.role.toLowerCase().includes(search.toLowerCase());
      const matchesDept = department === 'All' || emp.department === department;
      return matchesSearch && matchesDept;
    });
  }, [search, department, employees]);

  // Delete handler (demo)
  function handleDelete(id) {
    setEmployees((emps) => emps.filter((e) => e.id !== id));
  }

  // Responsive: show table on md+, cards on mobile
  return (
    <MainLayout>
      <div className="min-h-screen bg-gray-50 pb-20">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-700 py-10 px-4 md:px-0">
          <div className="max-w-7xl mx-auto">
            <div className="backdrop-blur-sm bg-white/10 rounded-xl px-6 py-7 md:py-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4 mx-3">
              <div>
                <h1 className="text-3xl font-bold text-white">Employee Directory</h1>
                <div className="text-indigo-200 mt-1">Manage your team members</div>
              </div>
              {/* Search & Filter */}
              <div className="flex flex-col md:flex-row gap-3 md:gap-4 w-full md:w-auto">
                <div className="bg-white/20 backdrop-blur-md rounded-full px-4 py-2 flex items-center w-full md:w-72">
                  <Search className="h-5 w-5 text-white" />
                  <input
                    className="bg-transparent border-0 text-white placeholder-indigo-100 focus:ring-0 ml-2 w-full outline-none"
                    placeholder="Search by name, department, or role..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                  />
                </div>
                <div className="relative">
                  <select
                    className="appearance-none bg-white/30 backdrop-blur-lg rounded-full pl-4 pr-10 py-2 text-indigo-900 font-medium focus:ring-2 focus:ring-indigo-400 border-0 transition min-w-[120px]"
                    value={department}
                    onChange={e => setDepartment(e.target.value)}
                  >
                    {DEPARTMENTS.map(dep => (
                      <option key={dep} value={dep}>{dep}</option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-indigo-400 pointer-events-none" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 mt-8">
          {/* Table for md+ */}
          <div className="hidden md:block">
            {filtered.length === 0 ? (
              <div className="text-center py-16">
                <Users className="mx-auto h-16 w-16 text-gray-300" />
                <h3 className="mt-4 text-lg font-medium text-gray-900">No employees</h3>
                <p className="mt-1 text-gray-500">Add your first team member to get started</p>
              </div>
            ) : (
              <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">EMPLOYEE</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">DEPARTMENT</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">ROLE</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">JOIN DATE</th>
                      <th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">ACTIONS</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filtered.map(employee => (
                      <tr key={employee.id} className="hover:bg-gray-50/80 transition duration-200 even:bg-gray-50/30">
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <Avatar src={employee.profilePic} name={employee.name} />
                            <div className="ml-4">
                              <p className="font-medium text-gray-800">{employee.name}</p>
                              <p className="text-sm text-gray-500">{employee.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${departmentColors[employee.department] || 'bg-gray-100 text-gray-800'}`}>
                            {employee.department}
                          </span>
                        </td>
                        <td className="px-6 py-4 font-medium text-gray-700">{employee.role}</td>
                        <td className="px-6 py-4 text-gray-500">{employee.joiningDate}</td>
                        <td className="px-6 py-4 text-right space-x-2">
                          <IconButton icon={Pencil} tooltip="Edit" color="blue" onClick={() => navigate(`/employees/edit/${employee.id}`)} />
                          <IconButton icon={Trash} tooltip="Delete" color="red" onClick={() => handleDelete(employee.id)} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          {/* Cards for mobile */}
          <div className="md:hidden grid grid-cols-1 gap-4">
            {filtered.length === 0 ? (
              <div className="text-center py-16">
                <Users className="mx-auto h-16 w-16 text-gray-300" />
                <h3 className="mt-4 text-lg font-medium text-gray-900">No employees</h3>
                <p className="mt-1 text-gray-500">Add your first team member to get started</p>
              </div>
            ) : (
              filtered.map(employee => (
                <div key={employee.id} className="bg-white rounded-xl p-4 shadow-md border border-gray-100 flex flex-col gap-2">
                  <div className="flex items-center space-x-3">
                    <Avatar src={employee.profilePic} name={employee.name} size="md" />
                    <div>
                      <h3 className="font-semibold text-gray-800">{employee.name}</h3>
                      <p className="text-sm text-gray-600">{employee.role}</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${departmentColors[employee.department] || 'bg-gray-100 text-gray-800'}`}>{employee.department}</span>
                    <span className="text-xs text-gray-500">{employee.email}</span>
                    <span className="text-xs text-gray-500">{employee.joiningDate}</span>
                  </div>
                  <div className="flex gap-2 mt-2">
                    <IconButton icon={Pencil} tooltip="Edit" color="blue" onClick={() => navigate(`/employees/edit/${employee.id}`)} />
                    <IconButton icon={Trash} tooltip="Delete" color="red" onClick={() => handleDelete(employee.id)} />
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
        {/* Add Employee FAB */}
        <button
          className="fixed bottom-8 right-8 bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-4 rounded-full shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-0.5 z-50"
          onClick={() => navigate('/employees/add')}
          title="Add Employee"
        >
          <Plus className="h-6 w-6" />
        </button>
      </div>
    </MainLayout>
  );
}
